import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Table2, Calculator, FileText, Printer } from "lucide-react";
import { PageHeader, StatPill, PeriodInput, Loading } from "@/components/common";
import { AuditTable } from "@/components/AuditTable";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { currentPeriod, num, periodLabel, rupiah } from "@/lib/format";
import { ReconBadge } from "@/components/badges";

const STEPS = [
  { to: "/rekap", icon: Table2, label: "Rekap Penjualan", note: "Input qty per pelanggan" },
  { to: "/estimasi", icon: Calculator, label: "Estimasi +30%", note: "Cadangan produksi" },
  { to: "/invoice", icon: FileText, label: "Invoice Akhir", note: "Pembulatan kelipatan 5" },
  { to: "/cetak", icon: Printer, label: "Cetak Bulk Nota", note: "A4, beberapa nota/halaman" },
];

function FlowSteps() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 border border-slate-300 bg-white rounded-sm" data-testid="flow-steps">
      {STEPS.map((s, i) => (
        <Link key={s.to} to={s.to} className="group p-4 border-r border-b md:border-b-0 border-slate-200 last:border-r-0 hover:bg-emerald-50 transition-colors" data-testid={`flow-step-${i + 1}`}>
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] text-emerald-700">LANGKAH {i + 1}</span>
            <ArrowRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
          </div>
          <s.icon className="h-5 w-5 mt-3 text-emerald-700" />
          <div className="font-display font-semibold mt-2">{s.label}</div>
          <div className="text-xs text-slate-500">{s.note}</div>
        </Link>
      ))}
    </div>
  );
}

function ReportSummaries({ r }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="dashboard-summaries">
      <div className="border border-slate-300 bg-white rounded-sm p-4 space-y-3" data-testid="summary-a">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-display font-semibold">A · Uang Penjualan Aktual & Top Up</h2>
          <ReconBadge status={r.status} testId="summary-a-status" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <StatPill label="Voucher Terjual Aktual" value={num(r.sales.qty)} tone="blue" testId="sa-qty" />
          <StatPill label="Uang Penjualan Aktual" value={rupiah(r.sales.value)} tone="blue" testId="sa-value" />
          <StatPill label="Top Up Aktual" value={rupiah(r.sales.topup)} tone="green" testId="sa-topup" />
          <StatPill label="Selisih Top Up" value={rupiah(r.sales.diff)} tone={r.sales.diff < 0 ? "amber" : "slate"} testId="sa-diff" />
        </div>
        <div className="text-xs text-slate-600">Pelanggan belum direkonsiliasi: <b data-testid="sa-unreconciled">{num(r.sales.unreconciled_customers)}</b> · <Link to="/rekonsiliasi" className="text-emerald-700 underline">Buka Rekonsiliasi</Link></div>
      </div>
      <div className="border border-slate-300 bg-white rounded-sm p-4 space-y-3" data-testid="summary-b">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-display font-semibold">B · Kebutuhan Pasokan Voucher</h2>
          <span className="text-[10px] text-slate-500">{r.supply.source === "estimasi" ? "dari estimasi terakhir" : "pratinjau (belum ada estimasi)"}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <StatPill label="Estimasi Reguler" value={num(r.supply.regular_qty)} tone="green" testId="sb-regular" />
          <StatPill label="Pasokan Awal Baru" value={num(r.supply.initial_new_qty)} testId="sb-initial-new" />
          <StatPill label="Pasokan Awal Aktif Kembali" value={num(r.supply.initial_reactivated_qty)} testId="sb-initial-react" />
          <StatPill label="Pasokan Final (invoice sah / termasuk draft)" value={`${num(r.supply.final_approved_qty)} / ${num(r.supply.final_qty)}`} tone="green" testId="sb-final" />
        </div>
        <div className="text-xs text-slate-600">Berhenti, tanpa pasokan berikutnya: <b data-testid="sb-stopped">{num(r.supply.stopped_customers)}</b> pelanggan · Pasokan untuk {periodLabel(r.supply_period.period)}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { tenant } = useAuth();
  const [period, setPeriod] = useState(currentPeriod());
  const [d, setD] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/dashboard", { params: { period } }).then((r) => live && setD(r.data)).catch((e) => toast.error(errMsg(e)));
    return () => { live = false; };
  }, [period]);
  return (
    <div>
      <PageHeader sheet="SHEET 01" title={`Dashboard ${tenant?.name || ""}`} subtitle={`Ringkasan periode ${periodLabel(period)}`}>
        <PeriodInput value={period} onChange={setPeriod} />
      </PageHeader>
      {!d ? <Loading /> : (
        <div className="space-y-6 fade-up">
          <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
            <StatPill label="Pelanggan Aktif (bukan berhenti)" value={num(d.customers)} testId="stat-customers" />
            <StatPill label="Jenis Voucher" value={num(d.voucher_types)} testId="stat-voucher-types" />
            <StatPill label="Qty Terjual" value={num(d.recap_qty)} tone="blue" testId="stat-recap-qty" />
            <StatPill label="Nilai Rekap" value={rupiah(d.recap_value)} tone="blue" testId="stat-recap-value" />
            <StatPill label="Invoice Draft / Sah" value={`${d.invoices_draft} / ${d.invoices_approved}`} tone="amber" testId="stat-invoices" />
            <StatPill label="Total Invoice Sah" value={rupiah(d.invoice_total)} tone="green" testId="stat-invoice-total" />
            <Link to="/hutang" className="contents" data-testid="stat-debt-link"><StatPill label="Hutang Pelanggan" value={rupiah(d.debt_total)} tone="amber" testId="stat-debt-total" /></Link>
          </div>
          <FlowSteps />
          {d.report && <ReportSummaries r={d.report} />}
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6 [&>*]:min-w-0">
            <div>
              <h2 className="font-display font-semibold text-lg mb-2">Penjualan per Jenis Voucher</h2>
              <div className="xl-wrap">
                <table className="xl">
                  <thead><tr><th className="rownum">#</th><th>Jenis Voucher</th><th className="text-right">Qty</th><th className="text-right">Nilai (Rp)</th></tr></thead>
                  <tbody>
                    {d.by_type.length === 0 && <tr><td colSpan={4} className="text-center text-slate-500 py-6">Belum ada rekap periode ini</td></tr>}
                    {d.by_type.map((t, i) => (
                      <tr key={t.name}><td className="rownum">{i + 1}</td><td>{t.name}</td><td className="num">{num(t.qty)}</td><td className="num">{num(t.value)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {d.recent.length > 0 && (
              <div>
                <h2 className="font-display font-semibold text-lg mb-2">Aktivitas Terakhir</h2>
                <AuditTable rows={d.recent} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
